import { Box, CircularProgress, Grid, Typography } from "@mui/material";
import { FDS_SubscriptionTypes } from "constants/index";
import { useFactsetStreaming } from "features/store";
import { useEffect, useState } from "react";
import OptionChain from "./OptionChain";
import StockSymbolAutocomplete from "./StockSymbolAutocomplete";

// How long the page waits on ONE streamed frame before it stops calling this
// loading. Asserted rather than measured — it is the point at which waiting
// stops looking like loading. Spent once per frame, not once per symbol: see
// the wait effect below for why the two round trips cannot share a budget.
const SETTLE_WAIT_MS = 5000;

// Wire-format names the FactSet gateway stamps on every frame
// (FDS_Streamer.putStreamerInfo). Everything else in a bucket is data.
const FRAME_META_KEYS = ["subscription_type", "streamer_key"];

export const NO_OPTIONS_TEXT = "No options are available for the selected stock";

export const OptionChainTab = () => {
  const [stockSymbol, setStockSymbol] = useState<string>("");
  const [hasSettled, setHasSettled] = useState<boolean>(false);

  const {
    dataCache: {
      [FDS_SubscriptionTypes.STOCK_PRICE]: stockPrices,
      [FDS_SubscriptionTypes.OPTION_CHAIN]: optionChain,
    },
  } = useFactsetStreaming();

  // The chain's own "N/A" test, asked one level up. Deliberately per-symbol:
  // cacheMessage replaces the STOCK_PRICE bucket wholesale, so "the bucket is
  // empty" would be suppressed by a payload for some other symbol.
  const hasPrice = stockSymbol !== "" && stockSymbol in stockPrices;

  // A price is not an option chain: the picker's universe is every Common Stock
  // and ETF row, which is far wider than the set of optionable names, so a
  // priced symbol with no listed groups is an ordinary outcome and the one this
  // page's message actually describes.
  //
  // Per-symbol for the same reason as hasPrice, but asked differently. The
  // bucket's contract keys are OSI symbols the gateway pads to six characters
  // (Util.getOSIsymbol in the FactSet gateway), so a prefix test on them would
  // match the wrong stock; the payload's own streamer_key — "OPTION_CHAIN:
  // <symbol>", stamped by FDS_Streamer on every frame — is what names the
  // symbol the bucket belongs to. Everything else in it is one contract, which
  // is exactly how OptionChain's optionGroups memo reads the bucket back.
  const hasContracts =
    stockSymbol !== "" &&
    optionChain.streamer_key ===
      `${FDS_SubscriptionTypes.OPTION_CHAIN}:${stockSymbol}` &&
    Object.keys(optionChain).some((key) => !FRAME_META_KEYS.includes(key));

  // Latched per committed symbol: once a chain has been drawn for this stock we
  // never take the verdict back. hasContracts can flip true->false long after
  // the chain is up — narrowing the strike range re-issues the request, and a
  // band with no listed strikes comes back as the same empty-but-addressed
  // frame an optionless stock does — and retracting there would hide the very
  // control the user needs to widen the band again, with no way back except
  // picking a different symbol. "No options" is a claim about the stock, not
  // about the window currently being asked for.
  const [hasSeenContracts, setHasSeenContracts] = useState<boolean>(false);

  useEffect(() => setHasSeenContracts(false), [stockSymbol]);

  useEffect(() => {
    if (hasContracts) {
      setHasSeenContracts(true);
    }
  }, [hasContracts]);

  // Restarted when the price lands, because the two frames arrive in series
  // rather than in parallel: OptionChain cannot even ASK for the chain until a
  // price has given it a strike range (ocRequestParams is null until then), so
  // the chain's round trip begins where the price's ends. One budget spanning
  // both would quietly shrink the chain's share to whatever the price left
  // over, and a slow-but-optionable stock would be told it has no options.
  // hasPrice flips at most once per committed symbol, so this is one restart.
  useEffect(() => {
    setHasSettled(false);
    if (stockSymbol === "") {
      return;
    }
    const timer = setTimeout(() => setHasSettled(true), SETTLE_WAIT_MS);
    return () => clearTimeout(timer);
  }, [stockSymbol, hasPrice]);

  // Three states, not two: settling, chain, no-data. Written against showChain
  // rather than against hasPrice so they stay exhaustive and mutually exclusive
  // now that the chain needs two frames, not one. The message never sticks once
  // the data lands: a late chain frame swaps straight to the chain, and a late
  // price reopens settling (it is what lets the chain be asked for at all), so
  // the sequence is message -> spinner -> verdict rather than a dead end.
  //
  // Note showChain === false is not only "asked and got nothing": OptionChain
  // never issues the request at all when parseInt(last_price) is 0 or NaN —
  // every stock under $1, and any "N/A" the gateway fills in for an absent
  // field. Those are told they have no options without having been asked
  // about. The cause is in OptionChain, which this task does not touch, and no
  // fix available here would do better than relabel it — so it is named as a
  // follow-up rather than half-fixed.
  const showChain = hasPrice && (hasContracts || hasSeenContracts);
  const isSettling = stockSymbol !== "" && !showChain && !hasSettled;
  const showNoOptions = stockSymbol !== "" && !showChain && hasSettled;

  return (
    <Box>
      <Grid container>
        <Grid container paddingY={2} alignItems="center">
          <Grid item xs={12} sm="auto">
            <StockSymbolAutocomplete onSymbolChange={setStockSymbol} />
          </Grid>
        </Grid>
      </Grid>
      <Grid container>
        <Box component="div" sx={{ width: "100%" }}>
          {isSettling && <CircularProgress sx={{ marginY: 1 }} />}
          {showNoOptions && (
            <Typography sx={{ marginY: 1 }}>{NO_OPTIONS_TEXT}</Typography>
          )}
          {/*
            OptionChain returns a Fragment and takes no sx/className, so it
            cannot hide itself. Hiding the wrapper keeps it mounted instead of
            unmounting it, which — with key={stockSymbol} already bounding the
            subscription to one — strictly reduces socket churn.
          */}
          {stockSymbol !== "" && (
            <Box sx={{ display: showChain ? "block" : "none" }}>
              <OptionChain stockSymbol={stockSymbol} key={stockSymbol} />
            </Box>
          )}
        </Box>
      </Grid>
    </Box>
  );
};
